import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  Clock, 
  Database, 
  Eye, 
  Filter,
  Download,
  Trash2,
  Users,
  Server,
  ShieldCheck,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { logsService } from '../../services/api';
import ActivityLogsList from './ActivityLogsList';

interface LogsStats {
  total_activities: number;
  total_system_logs: number;
  total_login_attempts: number;
  total_performance_metrics: number;
  activities_today: number;
  activities_week: number;
  activities_month: number;
  severity_stats: { [key: string]: number };
  module_stats: { [key: string]: number };
  login_success_rate: number;
  failed_logins_today: number;
  avg_response_time: number;
  slow_requests_count: number;
}

interface ActivityLog {
  id: number;
  user_display: string;
  action_type: string;
  action_type_display: string;
  action_description: string;
  severity: string;
  severity_display: string;
  module: string;
  timestamp: string;
  time_ago: string;
  ip_address?: string;
}

interface SystemLog {
  id: number;
  level: string;
  level_display: string;
  message: string;
  module: string;
  function: string;
  timestamp: string;
  time_ago: string;
  user?: { full_name: string } | null;
  extra_data?: Record<string, any>;
  stack_trace?: string;
}

interface LoginAttempt {
  id: number;
  email: string;
  ip_address: string;
  status: string;
  status_display: string;
  timestamp: string;
  time_ago: string;
  failure_reason?: string;
  is_suspicious: boolean;
}

const LogsDashboard: React.FC = () => {
  const [stats, setStats] = useState<LogsStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<ActivityLog[]>([]);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'activities' | 'system' | 'security'>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    if (selectedTab === 'overview') {
      fetchRecentActivities();
    } else if (selectedTab === 'system') {
      fetchSystemLogs();
    } else if (selectedTab === 'security') {
      fetchLoginAttempts();
    }
  }, [selectedTab]);

  const fetchStats = async () => {
    try {
      const response = await logsService.getStats();
      setStats(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
      console.error('Erreur stats:', err);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const response = await logsService.getActivities({ page_size: 10 });
      setRecentActivities(response.data.results || []);
    } catch (err) {
      console.error('Erreur activités récentes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemLogs = async () => {
    try {
      const response = await logsService.getSystemLogs({ page_size: 50 });
      setSystemLogs(response.data.results || []);
    } catch (err) {
      console.error('Erreur logs système:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginAttempts = async () => {
    try {
      const response = await logsService.getLoginAttempts({ page_size: 50 });
      setLoginAttempts(response.data.results || []);
    } catch (err) {
      console.error('Erreur tentatives connexion:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'critical': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType) {
      case 'login': return <Users className="h-4 w-4" />;
      case 'create': return <Activity className="h-4 w-4" />;
      case 'update': return <Clock className="h-4 w-4" />;
      case 'delete': return <Trash2 className="h-4 w-4" />;
      case 'read': return <Eye className="h-4 w-4" />;
      default: return <Database className="h-4 w-4" />;
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level.toUpperCase()) {
      case 'DEBUG': return 'text-gray-600 bg-gray-100';
      case 'INFO': return 'text-blue-600 bg-blue-100';
      case 'WARNING': return 'text-yellow-600 bg-yellow-100';
      case 'ERROR': return 'text-red-600 bg-red-100';
      case 'CRITICAL': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string): string => {
    if (status.includes('success')) return 'text-green-600 bg-green-100';
    if (status.includes('failed')) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow h-32"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Activity className="h-8 w-8 mr-3 text-blue-600" />
            Logs et Traçabilité
          </h1>
          <p className="mt-2 text-gray-600">
            Visualisez et analysez toutes les activités de votre application
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
              { id: 'activities', label: 'Activités', icon: Activity },
              { id: 'system', label: 'Système', icon: Server },
              { id: 'security', label: 'Sécurité', icon: ShieldCheck },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id as any)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    selectedTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {selectedTab === 'overview' && stats && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Activity className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Activités
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.total_activities.toLocaleString()}
                        </dd>
                        <dd className="text-sm text-gray-500">
                          {stats.activities_today} aujourd'hui
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Server className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Logs Système
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.total_system_logs.toLocaleString()}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Taux de Connexion
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.login_success_rate}%
                        </dd>
                        <dd className="text-sm text-red-600">
                          {stats.failed_logins_today} échecs aujourd'hui
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Clock className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Temps de Réponse Moyen
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.avg_response_time}ms
                        </dd>
                        <dd className="text-sm text-yellow-600">
                          {stats.slow_requests_count} requêtes lentes
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Severity Distribution */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Répartition par Sévérité
                </h3>
                <div className="space-y-4">
                  {Object.entries(stats.severity_stats).map(([severity, count]) => {
                    const total = Object.values(stats.severity_stats).reduce((a, b) => a + b, 0);
                    const percentage = ((count / total) * 100).toFixed(1);
                    
                    return (
                      <div key={severity} className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${getSeverityColor(severity).split(' ')[1]}`}></div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium capitalize">{severity}</span>
                            <span className="text-gray-500">{count} ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className={`h-2 rounded-full ${getSeverityColor(severity).split(' ')[1]}`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Module Activity */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Activité par Module
                </h3>
                <div className="space-y-3">
                  {Object.entries(stats.module_stats).slice(0, 8).map(([module, count]) => {
                    const maxCount = Math.max(...Object.values(stats.module_stats));
                    const percentage = ((count / maxCount) * 100).toFixed(1);
                    
                    return (
                      <div key={module} className="flex items-center">
                        <div className="w-24 text-sm font-medium text-gray-900 truncate">
                          {module}
                        </div>
                        <div className="flex-1 ml-4">
                          <div className="flex justify-between text-sm">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="ml-2 text-gray-500">{count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Content based on selected tab */}
        {selectedTab === 'activities' && <ActivityLogsList />}

        {selectedTab === 'overview' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Activités Récentes
                </h3>
              </div>

              <div className="flow-root">
                <ul className="-mb-8">
                  {recentActivities.map((activity, activityIdx) => (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== recentActivities.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getSeverityColor(activity.severity)}`}>
                              {getActionTypeIcon(activity.action_type)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-900">
                                  {activity.user_display}
                                </span>{' '}
                                {activity.action_description}
                              </p>
                              <div className="mt-1 flex items-center text-xs text-gray-400 space-x-4">
                                <span>{activity.module}</span>
                                {activity.ip_address && (
                                  <span>IP: {activity.ip_address}</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              <time dateTime={activity.timestamp}>
                                {activity.time_ago}
                              </time>
                              <div className="text-xs text-gray-400">
                                {format(new Date(activity.timestamp), 'dd MMM HH:mm', { locale: fr })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {recentActivities.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Aucune activité récente
                  </h3>
                  <p className="text-sm text-gray-500">
                    Les activités récentes apparaîtront ici
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Logs Tab */}
        {selectedTab === 'system' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Logs Système Récents
                </h3>
              </div>

              <div className="space-y-4">
                {systemLogs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(log.level)}`}>
                            <Server className="h-3 w-3 mr-1" />
                            {log.level_display}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {log.module}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-900">{log.message}</p>
                        <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                          <span>Fonction: {log.function}</span>
                          {log.user && <span>Utilisateur: {log.user.full_name}</span>}
                        </div>
                        {(log.stack_trace || Object.keys(log.extra_data || {}).length > 0) && (
                          <details className="mt-2 text-xs text-gray-600">
                            <summary className="cursor-pointer">Détails techniques</summary>
                            <pre className="mt-1 bg-gray-100 p-3 rounded overflow-auto max-h-60">
                              {JSON.stringify(log.extra_data, null, 2)}
                              {log.stack_trace ? `\n\n${log.stack_trace}` : ''}
                            </pre>
                          </details>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <div>{log.time_ago}</div>
                        <div className="text-xs text-gray-400">
                          {format(new Date(log.timestamp), 'dd MMM HH:mm', { locale: fr })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {systemLogs.length === 0 && (
                <div className="text-center py-8">
                  <Server className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Aucun log système récent
                  </h3>
                  <p className="text-sm text-gray-500">
                    Les logs système apparaîtront ici
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {selectedTab === 'security' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Tentatives de Connexion Récentes
                </h3>
              </div>

              <div className="space-y-4">
                {loginAttempts.map((attempt) => (
                  <div key={attempt.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(attempt.status)}`}>
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            {attempt.status_display}
                          </span>
                          {attempt.is_suspicious && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-orange-600 bg-orange-100">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Suspect
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm text-gray-900">Email: {attempt.email}</p>
                        <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                          <span>IP: {attempt.ip_address}</span>
                          {attempt.failure_reason && (
                            <span>Raison: {attempt.failure_reason}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <div>{attempt.time_ago}</div>
                        <div className="text-xs text-gray-400">
                          {format(new Date(attempt.timestamp), 'dd MMM HH:mm', { locale: fr })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {loginAttempts.length === 0 && (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Aucune tentative récente
                  </h3>
                  <p className="text-sm text-gray-500">
                    Les tentatives de connexion apparaîtront ici
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsDashboard;